import { computePosition } from '@floating-ui/dom'
import {
    defaultSuggestionCardFilter,
    SuggestionDataMap,
    useSuggestionsShallowSelector,
} from 'components/WorkspaceEditor/SuggestionStore'
import { debounce } from 'lodash'
import { useCallback, useEffect, useMemo, useRef } from 'react'

/**
 * Custom hook to track and update positions of suggestion cards in the UI.
 * It handles loading states, filtering, and debounced updates of card positions.
 *
 * @returns {Object} An object containing state and functions related to suggestion cards.
 */
export const useTrackChangeCards = () => {
    const {
        suggestionsData,
        isLoadingCards,
        suggestionCardFilter,
    } = useSuggestionsShallowSelector((s) => ({
        suggestionsData: s.suggestionData,
        isLoadingCards: s.isLoadingCards,
        suggestionCardFilter: s.suggestionCardFilter,
    }))
    const prevCardBottomRef = useRef(0)
    const rafRef = useRef<number | null>(null)

    const isNotDefaultSuggestionCardFilter = useMemo(
        () => suggestionCardFilter !== defaultSuggestionCardFilter,
        [suggestionCardFilter]
    )

    const shouldShowLoadingCards = isLoadingCards

    const flatSuggestions = useMemo(
        () =>
            Object.values(suggestionsData || {})?.length > 0
                ? Object.values(suggestionsData || {}).flatMap(
                      (group) => group?.suggestions || []
                  )
                : [],
        [suggestionsData]
    )

    const flatSuggestionsRef = useRef(flatSuggestions)
    const suggestionsDataRef = useRef(suggestionsData)

    flatSuggestionsRef.current = flatSuggestions
    suggestionsDataRef.current = suggestionsData

    const updateCardPositions = useCallback(
        async (overrideSuggestions?: Record<string, SuggestionDataMap>) => {
            prevCardBottomRef.current = 0

            const allSuggestions = overrideSuggestions
                ? Object.values(overrideSuggestions).flatMap(
                      (g) => g?.suggestions || []
                  )
                : flatSuggestionsRef.current
            const suggestionPositions: {
                id: string
                top: number
                card: HTMLElement
                target: HTMLElement
            }[] = []

            for (const suggestion of allSuggestions) {
                const id = suggestion?.id
                const target = document.querySelector(
                    `[data-suggestion='content-suggestion-id-${id}']`
                ) as HTMLElement
                const card = document.querySelector(
                    `[data-suggestion-id='${id}']`
                ) as HTMLElement
                if (!target || !card) {
                    if (card) {
                        card.classList.remove('visible')
                        card.style.top = '0px'
                    }
                    continue
                }

                const { y } = await computePosition(target, card)
                suggestionPositions.push({ id, top: y, card, target })
            }

            // Sort cards vertically
            suggestionPositions.sort((a, b) => a.top - b.top)
            const positions: { id: string; top: number }[] = []

            for (const { id, top, card } of suggestionPositions) {
                const cardHeight = card.offsetHeight
                let adjustedTop = top - 50
                if (adjustedTop < prevCardBottomRef.current) {
                    adjustedTop = prevCardBottomRef.current + 8
                }
                prevCardBottomRef.current = adjustedTop + cardHeight
                positions.push({ id, top: adjustedTop })
            }

            // Cancel pending animation
            if (rafRef.current) {
                cancelAnimationFrame(rafRef.current)
                rafRef.current = null
            }

            // Animate position updates
            rafRef.current = requestAnimationFrame(() => {
                positions.forEach(({ id, top }) => {
                    const el = document.querySelector(
                        `[data-suggestion-id='${id}']`
                    ) as HTMLElement
                    if (el) {
                        el.style.top = `${top}px`
                        el.classList.add('visible')
                    }
                })
            })
        },
        []
    )

    const debouncedUpdateCardPositions = useMemo(
        () => debounce(updateCardPositions, 300),
        [updateCardPositions]
    )

    useEffect(() => {
        // Cleanup animation frame on unmount
        return () => {
            if (rafRef.current) {
                cancelAnimationFrame(rafRef.current)
                rafRef.current = null
            }
        }
    }, [])

    return {
        shouldShowLoadingCards,
        isNotDefaultSuggestionCardFilter,
        debouncedUpdateCardPositions,
        suggestionsData,
    }
}
